import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  User, 
  Mail, 
  Shield, 
  Bell, 
  Globe,
  Key,
  Users,
  Settings as SettingsIcon,
  MessageCircle,
  ShoppingBag,
  Plus,
  UserPlus,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = [
  { value: "admin", label: "Admin", color: "bg-red-100 text-red-800", description: "Full access to all features" },
  { value: "team_lead", label: "Team Lead", color: "bg-purple-100 text-purple-800", description: "Can view team reports and assign leads" },
  { value: "caller", label: "Caller/Agent", color: "bg-blue-100 text-blue-800", description: "Can manage assigned leads" },
];

export default function SettingsPage({ user, logout }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile");
  const [myRole, setMyRole] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "caller" });

  useEffect(() => {
    fetchMyRole();
  }, []);

  useEffect(() => {
    if (activeTab === "team") {
      fetchTeam();
    }
  }, [activeTab]);

  const fetchMyRole = async () => {
    try {
      const response = await axios.get(`${API}/team/my-role`, { withCredentials: true });
      setMyRole(response.data.user);
    } catch (error) {
      console.error("Failed to fetch role");
    }
  };

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/team/users`, { withCredentials: true });
      setTeam(response.data.users || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error("Failed to load team members");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/team/users/${userId}`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated successfully");
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.name) {
      toast.error("Name and email are required");
      return;
    }
    try {
      await axios.post(`${API}/team/users`, newUser, { withCredentials: true });
      toast.success("User added successfully");
      setShowAddUserModal(false);
      setNewUser({ name: "", email: "", role: "caller" });
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add user");
    }
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      await axios.put(`${API}/team/users/${userId}`, { is_active: !isActive }, { withCredentials: true });
      toast.success(isActive ? "User deactivated" : "User activated");
      fetchTeam();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const getRoleColor = (role) => {
    return roles.find(r => r.value === role)?.color || "bg-slate-100 text-slate-800";
  };

  const tabs = [
    { id: "profile", label: "Profile", icon: User },
    { id: "team", label: "Team", icon: Users },
    { id: "integrations", label: "Integrations", icon: Globe },
    { id: "notifications", label: "Notifications", icon: Bell },
  ];

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Settings</h1>
          <p className="text-slate-600 mt-1">Manage your account and platform settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <Card className="lg:col-span-1 h-fit">
            <CardContent className="p-4">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? "bg-teal-50 text-cyan-700 font-medium"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid={`settings-tab-${tab.id}`}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </CardContent>
          </Card>

          {/* Content */}
          <div className="lg:col-span-3">
            {/* Profile Tab */}
            {activeTab === "profile" && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold font-[Manrope]">Profile Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-6">
                    <Avatar className="w-20 h-20">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-cyan-100 text-cyan-700 text-2xl">
                        {user?.name?.charAt(0) || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900">{user?.name}</h3>
                      <p className="text-slate-500">{user?.email}</p>
                      <Badge className={getRoleColor(user?.role)} variant="secondary">
                        {user?.role}
                      </Badge>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="form-label">Full Name</label>
                      <Input value={user?.name || ""} disabled className="bg-slate-50" />
                    </div>
                    <div>
                      <label className="form-label">Email</label>
                      <Input value={user?.email || ""} disabled className="bg-slate-50" />
                    </div>
                    <div>
                      <label className="form-label">Role</label>
                      <Input value={user?.role || ""} disabled className="bg-slate-50" />
                    </div>
                    <div>
                      <label className="form-label">User ID</label>
                      <Input value={user?.user_id || ""} disabled className="bg-slate-50" />
                    </div>
                  </div>

                  <div className="p-4 bg-teal-50 rounded-lg">
                    <p className="text-sm text-cyan-800">
                      <Shield className="w-4 h-4 inline mr-2" />
                      Your account is secured with Google OAuth. Profile changes can be made through your Google account.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team Tab */}
            {activeTab === "team" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold font-[Manrope]">Team Management</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">Manage user roles and permissions</p>
                  </div>
                  {myRole?.role === "admin" && (
                    <Button 
                      className="bg-cyan-700 hover:bg-cyan-800"
                      onClick={() => setShowAddUserModal(true)}
                      data-testid="add-user-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add User
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {/* Role Legend */}
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium text-slate-700 mb-2">Role Permissions:</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {roles.map((role) => (
                        <div key={role.value} className="flex items-center gap-2">
                          <Badge className={role.color}>{role.label}</Badge>
                          <span className="text-xs text-slate-500">{role.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {myRole?.role !== "admin" && myRole?.role !== "team_lead" ? (
                    <div className="text-center py-12 text-slate-500">
                      <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>You need admin or team lead privileges to manage team members</p>
                    </div>
                  ) : loading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : team.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No team members yet</p>
                      <p className="text-sm mt-1">Add team members using the button above</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {team.map((member) => (
                        <div 
                          key={member.user_id}
                          className={`flex items-center justify-between p-4 rounded-xl ${member.is_active !== false ? 'bg-slate-50' : 'bg-red-50 opacity-60'}`}
                          data-testid={`team-member-${member.user_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <Avatar>
                              <AvatarImage src={member.picture} />
                              <AvatarFallback className="bg-cyan-100 text-cyan-700">
                                {member.name?.charAt(0) || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-slate-900">{member.name}</p>
                              <p className="text-sm text-slate-500">{member.email}</p>
                              {member.is_active === false && (
                                <Badge variant="outline" className="text-red-600 border-red-300 mt-1">Inactive</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {member.user_id === user?.user_id ? (
                              <Badge className={getRoleColor(member.role)}>{roles.find(r => r.value === member.role)?.label || member.role}</Badge>
                            ) : myRole?.role === "admin" ? (
                              <>
                                <Select 
                                  value={member.role} 
                                  onValueChange={(value) => handleUpdateRole(member.user_id, value)}
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {roles.map((role) => (
                                      <SelectItem key={role.value} value={role.value}>
                                        {role.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleUserStatus(member.user_id, member.is_active !== false)}
                                  className={member.is_active !== false ? "text-red-600 hover:text-red-700" : "text-green-600 hover:text-green-700"}
                                >
                                  {member.is_active !== false ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                                </Button>
                              </>
                            ) : (
                              <Badge className={getRoleColor(member.role)}>{roles.find(r => r.value === member.role)?.label || member.role}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Add User Modal */}
            <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add Team Member</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="form-label">Name *</label>
                    <Input
                      placeholder="John Doe"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label">Email *</label>
                    <Input
                      type="email"
                      placeholder="john@hearclear.in"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label">Role</label>
                    <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      {roles.find(r => r.value === newUser.role)?.description}
                    </p>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button variant="outline" className="flex-1" onClick={() => setShowAddUserModal(false)}>
                      Cancel
                    </Button>
                    <Button className="flex-1 bg-cyan-700 hover:bg-cyan-800" onClick={handleAddUser}>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add User
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Integrations Tab */}
            {activeTab === "integrations" && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold font-[Manrope]">Integrations</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* WhatsApp */}
                  <div className="flex items-center justify-between p-4 rounded-xl border">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">WhatsApp Business</p>
                        <p className="text-sm text-slate-500">Interakt API Integration</p>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-800">Connected</Badge>
                  </div>

                  {/* Shopify */}
                  <div className="flex items-center justify-between p-4 rounded-xl border">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <ShoppingBag className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Shopify</p>
                        <p className="text-sm text-slate-500">E-commerce Integration</p>
                      </div>
                    </div>
                    <Badge className="bg-yellow-100 text-yellow-800">Pending Setup</Badge>
                  </div>

                  {/* AI */}
                  <div className="flex items-center justify-between p-4 rounded-xl border">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                        <Key className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">AI Recommendations</p>
                        <p className="text-sm text-slate-500">GPT-5.2 Powered</p>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg mt-6">
                    <p className="text-sm text-slate-600">
                      <SettingsIcon className="w-4 h-4 inline mr-2" />
                      Integration settings are managed by administrators. Contact support for changes.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notifications Tab */}
            {activeTab === "notifications" && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold font-[Manrope]">Notification Preferences</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    {[
                      { label: "New Lead Alerts", desc: "Get notified when new leads come in", enabled: true },
                      { label: "WhatsApp Messages", desc: "Notifications for incoming WhatsApp messages", enabled: true },
                      { label: "Appointment Reminders", desc: "Reminders for upcoming appointments", enabled: true },
                      { label: "Weekly Reports", desc: "Receive weekly performance summary", enabled: false },
                      { label: "Product Updates", desc: "Updates about new features and improvements", enabled: false },
                    ].map((notif, index) => (
                      <div key={index} className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
                        <div>
                          <p className="font-medium text-slate-900">{notif.label}</p>
                          <p className="text-sm text-slate-500">{notif.desc}</p>
                        </div>
                        <div className={`w-12 h-6 rounded-full ${notif.enabled ? 'bg-cyan-700' : 'bg-slate-300'} relative cursor-pointer`}>
                          <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${notif.enabled ? 'right-0.5' : 'left-0.5'}`}></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button className="bg-cyan-700 hover:bg-cyan-800">
                    Save Preferences
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
